import type { ReactNode } from 'react';

import styles from './Badge.module.css';

/**
 * Тон бейджа — семантическая шкала DESIGN_SYSTEM §2.4. Цвет никогда не
 * единственный носитель смысла: подпись обязательна, иконка 14 px — слева.
 * Бейдж без иконки допустим только для нейтральных меток («Neu»).
 *
 * `action` окрашен синим акцентом. Красного тона у бейджа нет: красный — только
 * главное действие экрана (DESIGN_SYSTEM §2.2), а метка ничего не делает.
 *
 * `success` → `match`, `warning` → `compromise`, `ai` → `action` — переходные
 * псевдонимы старых экранов; в новом коде используются канонические имена.
 */
export type BadgeTone =
  | 'neutral'
  | 'action'
  | 'match'
  | 'compromise'
  | 'attention'
  | 'nodata'
  | 'info'
  | 'danger'
  | 'inverse'
  // Переходные псевдонимы (до редизайна экранов в эпиках 20–27):
  | 'success'
  | 'warning'
  | 'ai';

const TONE_CLASS: Record<BadgeTone, string> = {
  neutral: styles.neutral ?? '',
  action: styles.action ?? '',
  match: styles.match ?? '',
  compromise: styles.compromise ?? '',
  attention: styles.attention ?? '',
  nodata: styles.nodata ?? '',
  info: styles.info ?? '',
  danger: styles.danger ?? '',
  inverse: styles.inverse ?? '',
  success: styles.match ?? '',
  warning: styles.compromise ?? '',
  ai: styles.action ?? '',
};

interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone | undefined;
  icon?: ReactNode | undefined;
  uppercase?: boolean | undefined;
  className?: string | undefined;
}

export function Badge({
  children,
  tone = 'neutral',
  icon,
  uppercase = true,
  className,
}: BadgeProps) {
  return (
    <span
      className={[styles.badge, TONE_CLASS[tone], uppercase && styles.uppercase, className]
        .filter(Boolean)
        .join(' ')}
    >
      {icon ? (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}
